import { ChildDashboard, ChildActivity, ChoreListPeriodStatus, MarkItemCompleteResponse, DailyProgress, BehaviorRecord, Child } from '../types/api';
import { getDateInFamilyTimezone } from '../utils/dateUtils';

/**
 * Raw behavior record shape as returned by the backend before field-name normalization.
 * The backend sends both legacy (`type`, `createdAt`) and current (`behaviorType`, `recordedAt`)
 * field names depending on endpoint; callers normalize with `||` fallbacks below.
 */
interface RawBehaviorRecord {
  type?: number;
  behaviorType?: number;
  createdAt?: string;
  recordedAt?: string;
  [key: string]: unknown;
}

/**
 * Raw goal shape as returned by the backend before field-name normalization.
 * Mirrors the `||` fallback fields used when transforming into the frontend `Goal` type.
 */
interface RawGoal {
  id: number;
  goalId?: number;
  title: string;
  description?: string;
  goalType?: number;
  type?: number;
  targetValue: number;
  currentValue?: number;
  currentProgress?: number;
  status: number;
  startDate?: string;
  completedAt?: string;
  failedAt?: string;
  celebratedAt?: string;
  rewardDescription?: string;
  reward?: string;
  rewardImageUrl?: string;
  isRecurring?: boolean;
  isNukeExempt?: boolean;
  dayPasses?: number;
  dayPassesUsed?: number;
  daysRemaining?: number;
  endDate?: string;
  currentStreak?: number;
  createdAt?: string;
  updatedAt?: string;
}

export class ChildDashboardService {
  /**
   * Get behaviors for a child on a specific date using URL slug + token authentication
   */
  async getBehaviorsForDateByUrlSlugWithToken(urlSlug: string, token: string, date: string): Promise<BehaviorRecord[]> {
    const response = await fetch(`/api/child-dashboard/${urlSlug}/behaviors?date=${date}&token=${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('This dashboard link is no longer valid - ask your parent for a new one');
      } else if (response.status === 400) {
        throw new Error('Invalid date format');
      } else {
        throw new Error('Failed to load behavior data');
      }
    }

    const data = await response.json();
    return data.behaviors || [];
  }

  /**
   * Get behaviors for a child on a specific date using URL slug authentication (no token)
   */
  async getBehaviorsForDateByUrlSlug(urlSlug: string, date: string): Promise<BehaviorRecord[]> {
    const response = await fetch(`/api/child-dashboard/${urlSlug}/behaviors?date=${date}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('This dashboard link is no longer valid - ask your parent for a new one');
      } else if (response.status === 400) {
        throw new Error('Invalid date format');
      } else {
        throw new Error('Failed to load behavior data');
      }
    }

    const data = await response.json();
    return data.behaviors || [];
  }

  /**
   * Get heart dates for a child using URL slug + token authentication
   */
  async getHeartDatesByUrlSlugWithToken(urlSlug: string, token: string, startDate: Date, endDate: Date, familyTimezone?: string): Promise<{ heartDates: string[]; mendingHeartDates: string[] }> {
    const startDateParam = getDateInFamilyTimezone(startDate, familyTimezone);
    const endDateParam = getDateInFamilyTimezone(endDate, familyTimezone);

    const response = await fetch(`/api/child-dashboard/${urlSlug}/hearts?startDate=${startDateParam}&endDate=${endDateParam}&token=${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('This dashboard link is no longer valid - ask your parent for a new one');
      } else {
        throw new Error('Failed to load heart dates');
      }
    }

    const data = await response.json();
    return { heartDates: data.heartDates || [], mendingHeartDates: data.mendingHeartDates || [] };
  }

  /**
   * Get heart dates for a child using URL slug authentication (no token)
   */
  async getProgressHistoryByUrlSlugWithToken(urlSlug: string, token: string, startDate: Date, endDate: Date, familyTimezone?: string): Promise<DailyProgress[]> {
    const startDateParam = getDateInFamilyTimezone(startDate, familyTimezone);
    const endDateParam = getDateInFamilyTimezone(endDate, familyTimezone);
    const response = await fetch(`/api/child-dashboard/${urlSlug}/progress-history?startDate=${startDateParam}&endDate=${endDateParam}&token=${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) return [];
    return response.json();
  }

  async getProgressHistoryByUrlSlug(urlSlug: string, startDate: Date, endDate: Date, familyTimezone?: string): Promise<DailyProgress[]> {
    const startDateParam = getDateInFamilyTimezone(startDate, familyTimezone);
    const endDateParam = getDateInFamilyTimezone(endDate, familyTimezone);
    const response = await fetch(`/api/child-dashboard/${urlSlug}/progress-history?startDate=${startDateParam}&endDate=${endDateParam}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) return [];
    return response.json();
  }

  async getHeartDatesByUrlSlug(urlSlug: string, startDate: Date, endDate: Date, familyTimezone?: string): Promise<{ heartDates: string[]; mendingHeartDates: string[] }> {
    const startDateParam = getDateInFamilyTimezone(startDate, familyTimezone);
    const endDateParam = getDateInFamilyTimezone(endDate, familyTimezone);

    const response = await fetch(`/api/child-dashboard/${urlSlug}/hearts?startDate=${startDateParam}&endDate=${endDateParam}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('This dashboard link is no longer valid - ask your parent for a new one');
      } else {
        throw new Error('Failed to load heart dates');
      }
    }

    const data = await response.json();
    return { heartDates: data.heartDates || [], mendingHeartDates: data.mendingHeartDates || [] };
  }

  /**
   * Get dashboard data using token authentication (Issue #741)
   */
  async getDashboardDataByToken(urlSlug: string, token?: string): Promise<ChildDashboard> {
    const url = token
      ? `/api/child-dashboard/${urlSlug}?token=${encodeURIComponent(token)}`
      : `/api/child-dashboard/${urlSlug}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('This dashboard link is no longer valid - ask your parent for a new one');
      } else if (response.status === 401) {
        throw new Error('This dashboard link has expired');
      } else {
        throw new Error('Failed to load your dashboard');
      }
    }

    const backendData = await response.json();

    // API now returns data in the expected format - return directly
    const transformedData: ChildDashboard = {
      childId: backendData.childId,
      childName: backendData.childName,
      starCount: backendData.starCount || 0,
      triangleCount: backendData.triangleCount || 0,
      squareCount: backendData.squareCount || 0,
      flipYourCoinCount: backendData.flipYourCoinCount || 0,
      makeItRightCount: backendData.makeItRightCount || 0,
      oneOnOneTimeCount: backendData.oneOnOneTimeCount || 0,
      ladderPosition: backendData.ladderPosition || 1,
      todaysBehaviors: (backendData.todaysBehaviors || []).map((behavior: RawBehaviorRecord) => ({
        ...behavior,
        behaviorType: behavior.type || behavior.behaviorType,
        recordedAt: behavior.createdAt || behavior.recordedAt
      })),
      activeGoals: (backendData.activeGoals || []).map((goal: RawGoal) => ({
        id: goal.id,
        goalId: goal.goalId || goal.id,
        title: goal.title,
        description: goal.description || '',
        type: goal.goalType || goal.type,
        targetValue: goal.targetValue,
        currentValue: goal.currentValue || goal.currentProgress || 0,
        currentProgress: goal.currentProgress || goal.currentValue || 0,
        status: goal.status,
        startDate: goal.startDate,
        completedAt: goal.completedAt,
        failedAt: goal.failedAt,
        celebratedAt: goal.celebratedAt,
        rewardDescription: goal.rewardDescription || goal.reward,
        rewardImageUrl: goal.rewardImageUrl,
        isRecurring: goal.isRecurring || false,
        isNukeExempt: goal.isNukeExempt,
        dayPasses: goal.dayPasses,
        dayPassesUsed: goal.dayPassesUsed,
        daysRemaining: goal.daysRemaining,
        endDate: goal.endDate,
        currentStreak: goal.currentStreak || 0,
        familyId: backendData.familyId || 0,
        childId: backendData.childId,
        createdAt: goal.createdAt || '',
        updatedAt: goal.updatedAt || ''
      })),
      familyTimezone: backendData.familyTimezone,
      familyChildIndex: backendData.familyChildIndex,
    };

    return transformedData;
  }

  /**
   * Get dashboard data using new URL slug endpoint (Issue #617)
   */
  async getDashboardDataByUrlSlug(urlSlug: string): Promise<ChildDashboard> {
    const response = await fetch(`/api/child-dashboard/${urlSlug}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'This dashboard link is no longer valid - ask your parent for a new one');
      } else {
        throw new Error('Failed to load your dashboard');
      }
    }

    const backendData = await response.json();

    // Transform backend data to match ChildDashboard interface (API returns flat structure)
    const transformedData: ChildDashboard = {
      childId: backendData.childId,
      childName: backendData.childName,
      starCount: backendData.starCount || 0,
      triangleCount: backendData.triangleCount || 0,
      squareCount: backendData.squareCount || 0,
      flipYourCoinCount: backendData.flipYourCoinCount || 0,
      makeItRightCount: backendData.makeItRightCount || 0,
      oneOnOneTimeCount: backendData.oneOnOneTimeCount || 0,
      ladderPosition: backendData.ladderPosition || 1,
      todaysBehaviors: (backendData.todaysBehaviors || []).map((behavior: RawBehaviorRecord) => ({
        ...behavior,
        behaviorType: behavior.type || behavior.behaviorType,
        recordedAt: behavior.createdAt || behavior.recordedAt
      })),
      activeGoals: (backendData.activeGoals || []).map((goal: RawGoal) => ({
        id: goal.id,
        goalId: goal.goalId || goal.id,
        title: goal.title,
        description: goal.description || '',
        type: goal.goalType || goal.type,
        targetValue: goal.targetValue,
        currentValue: goal.currentValue || goal.currentProgress || 0,
        currentProgress: goal.currentProgress || goal.currentValue || 0,
        status: goal.status,
        startDate: goal.startDate,
        completedAt: goal.completedAt,
        failedAt: goal.failedAt,
        celebratedAt: goal.celebratedAt,
        rewardDescription: goal.rewardDescription || goal.reward,
        rewardImageUrl: goal.rewardImageUrl,
        isRecurring: goal.isRecurring || false,
        isNukeExempt: goal.isNukeExempt,
        dayPasses: goal.dayPasses,
        dayPassesUsed: goal.dayPassesUsed,
        daysRemaining: goal.daysRemaining,
        endDate: goal.endDate,
        currentStreak: goal.currentStreak || 0,
        familyId: backendData.familyId || 0,
        childId: backendData.childId,
        createdAt: goal.createdAt || '',
        updatedAt: goal.updatedAt || ''
      })),
      familyTimezone: backendData.familyTimezone,
      familyChildIndex: backendData.familyChildIndex,
    };

    return transformedData;
  }

  /**
   * Get dashboard data for a child using their access token
   */
  async getDashboardData(token: string, forceRefresh: boolean = false): Promise<ChildDashboard> {
    if (forceRefresh) {
      this.clearCache(token);
    }
    const response = await fetch(`/api/child-access/dashboard/${token}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Dashboard not found or invalid link');
      } else if (response.status === 401) {
        throw new Error('This dashboard link has expired');
      } else {
        throw new Error('Failed to load your dashboard');
      }
    }

    return response.json();
  }

  /**
   * Validate child access token and URL slug
   */
  async validateAccess(token: string, urlSlug: string): Promise<boolean> {
    try {
      const response = await fetch(`/api/child-access/validate/${token}/${urlSlug}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Error validating child access:', error);
      return false;
    }
  }

  /**
   * Get child information by access token (for initial setup)
   */
  async getChildByToken(token: string): Promise<Child> {
    const response = await fetch(`/api/child-access/child/${token}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to get child information');
    }

    return response.json();
  }

  /**
   * Complete child setup with 6-digit code
   */
  async completeSetup(token: string, setupCode: string): Promise<void> {
    const response = await fetch(`/api/child-access/setup/${token}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ setupCode }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Invalid setup code');
    }
  }

  /**
   * Log child activity for tracking and analytics
   */
  async logActivity(token: string, activity: ChildActivity): Promise<void> {
    try {
      const response = await fetch(`/api/child-access/activity/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(activity),
      });

      if (!response.ok) {
        console.warn('Failed to log child activity:', response.statusText);
      }
    } catch (error) {
      // Don't throw errors for activity logging failures
      console.warn('Error logging child activity:', error);
    }
  }

  /**
   * Extract token from the current URL path
   */
  getTokenFromUrl(): string | null {
    const path = window.location.pathname;
    const match = path.match(/\/family\/\d+\/\w+\/child\/\w+\/(\w+)$/);
    return match ? match[1] : null;
  }

  /**
   * Extract URL slug from the current URL path
   */
  getUrlSlugFromUrl(): string | null {
    const path = window.location.pathname;
    const match = path.match(/\/family\/\d+\/(\w+)\/child\/\w+\/\w+$/);
    return match ? match[1] : null;
  }

  /**
   * Extract URL slug from new child dashboard URL pattern (Issue #617)
   */
  getNewUrlSlugFromUrl(): string | null {
    const path = window.location.pathname;
    const match = path.match(/\/child-dashboard\/(\w+)$/);
    return match ? match[1] : null;
  }

  /**
   * Check if current URL is using the new child dashboard pattern (Issue #617)
   */
  isNewUrlSlugPattern(): boolean {
    return window.location.pathname.startsWith('/child-dashboard/');
  }

  /**
   * Extract child name from URL for display purposes
   */
  getChildNameFromUrl(): string {
    const path = window.location.pathname;
    const match = path.match(/\/family\/\d+\/\w+\/child\/(\w+)\/\w+$/);
    return match ? decodeURIComponent(match[1]) : 'Child';
  }

  /**
   * Get family ID from URL
   */
  getFamilyIdFromUrl(): number | null {
    const path = window.location.pathname;
    const match = path.match(/\/family\/(\d+)\/\w+\/child\/\w+\/\w+$/);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Format time for child-friendly display
   */
  formatTimeForChild(date: string): string {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  /**
   * Format date for child-friendly display
   */
  formatDateForChild(date?: string): string {
    const targetDate = date ? new Date(date) : new Date();
    return targetDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  /**
   * Get behavior name for child-friendly display
   */
  getBehaviorName(behaviorType: number): string {
    switch (behaviorType) {
      case 1:
        return 'Star';
      case 2:
        return 'Triangle';
      case 3:
        return 'Storm';
      case 4:
        return 'Reset';
      case 5:
        return 'Make It Right';
      case 6:
        return 'Let Go';
      default:
        return 'Unknown';
    }
  }

  /**
   * Check if current time is appropriate for child access
   * Can be used for parental controls in the future
   */
  isAccessTimeAllowed(): boolean {
    const now = new Date();
    const hour = now.getHours();
    
    // Basic time restriction: 6 AM to 9 PM
    // This can be made configurable per family in the future
    return hour >= 6 && hour < 21;
  }

  /**
   * Generate a cache key for dashboard data
   */
  getDashboardCacheKey(token: string): string {
    return `child_dashboard_${token}`;
  }

  /**
   * Simple cache implementation for dashboard data
   */
  private cache = new Map<string, { data: ChildDashboard; timestamp: number }>();
  private CACHE_DURATION = 10000; // 10 seconds

  /**
   * Get cached dashboard data if available and fresh
   */
  getCachedDashboardData(token: string): ChildDashboard | null {
    const cacheKey = this.getDashboardCacheKey(token);
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }
    
    return null;
  }

  /**
   * Cache dashboard data
   */
  cacheDashboardData(token: string, data: ChildDashboard): void {
    const cacheKey = this.getDashboardCacheKey(token);
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Clear cache for a specific token
   */
  clearCache(token: string): void {
    const cacheKey = this.getDashboardCacheKey(token);
    this.cache.delete(cacheKey);
  }

  /**
   * Clear all cached data
   */
  clearAllCache(): void {
    this.cache.clear();
  }

  /**
   * Get child behavior data for a specific date using token authentication
   */
  async getBehaviorsByDateWithToken(urlSlug: string, token: string, date: string, browserTimezone?: string): Promise<BehaviorRecord[]> {
    const params = new URLSearchParams();
    params.append('date', date);
    if (browserTimezone) params.append('browserTimezone', browserTimezone);
    params.append('token', token);

    const response = await fetch(`/api/child-dashboard/${urlSlug}/behaviors?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('This dashboard link is no longer valid - ask your parent for a new one');
      } else {
        throw new Error('Failed to load behavior data');
      }
    }

    const data = await response.json();
    return data.behaviors || [];
  }

  /**
   * Get child behavior data for a specific date using URL slug authentication (no token)
   */
  async getBehaviorsByDateWithUrlSlug(urlSlug: string, date: string, browserTimezone?: string): Promise<BehaviorRecord[]> {
    const params = new URLSearchParams();
    params.append('date', date);
    if (browserTimezone) params.append('browserTimezone', browserTimezone);

    const response = await fetch(`/api/child-dashboard/${urlSlug}/behaviors?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('This dashboard link is no longer valid - ask your parent for a new one');
      } else {
        throw new Error('Failed to load behavior data');
      }
    }

    const data = await response.json();
    return data.behaviors || [];
  }

  /**
   * Get chores for a child using URL slug authentication
   */
  async getChoresByUrlSlug(urlSlug: string, token?: string): Promise<ChoreListPeriodStatus[]> {
    const params = token ? `?token=${encodeURIComponent(token)}` : '';
    const response = await fetch(`/api/child-dashboard/${urlSlug}/responsibilities${params}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('This dashboard link is no longer valid - ask your parent for a new one');
      } else {
        throw new Error('Failed to load responsibilities');
      }
    }

    return response.json();
  }

  /**
   * Mark a chore item as complete using URL slug authentication
   */
  async markChoreItemComplete(urlSlug: string, choreListId: number, itemId: number, token?: string): Promise<MarkItemCompleteResponse> {
    const params = token ? `?token=${encodeURIComponent(token)}` : '';
    const response = await fetch(`/api/child-dashboard/${urlSlug}/responsibilities/${choreListId}/items/${itemId}/complete${params}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Item not found');
      } else {
        throw new Error('Failed to mark item as complete');
      }
    }

    return response.json() as Promise<MarkItemCompleteResponse>;
  }

  /**
   * Mark a chore item as incomplete using URL slug authentication
   */
  async markChoreItemIncomplete(urlSlug: string, choreListId: number, itemId: number, token?: string): Promise<void> {
    const params = token ? `?token=${encodeURIComponent(token)}` : '';
    const response = await fetch(`/api/child-dashboard/${urlSlug}/responsibilities/${choreListId}/items/${itemId}/complete${params}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Item not found');
      } else {
        throw new Error('Failed to mark item as incomplete');
      }
    }
  }
}

// Export a singleton instance
export const childDashboardService = new ChildDashboardService();